/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useConfiguration } from '../../../../context/configurationContext';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  ListingTypeModal,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import HeartOutlineIcon from '../../../../components/IconComponent/HeartOutlineIcon';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    onManageDisableScrolling,
  } = props;
  const [isListingTypeModalOpen, setIsListingTypeModalOpen] = useState(false);
  const config = useConfiguration();
  const history = useHistory();

  const user = ensureCurrentUser(currentUser);
const intl = useIntl();
  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <span
      className={css.createNewListingLink}
      onClick={() => setIsListingTypeModalOpen(true)}
      role="button"
      tabIndex={0}
    >
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </span>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    // const login = (
    //   <NamedLink name="LoginPage" className={css.loginLink}>
    //     <FormattedMessage id="TopbarMobileMenu.loginLink" />
    //   </NamedLink>
    // );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        {/* <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        /> */}
        {signup}
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <ul className={css.customLinksWrapper}>{extraLinks}</ul>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
        <ListingTypeModal
          isOpen={isListingTypeModalOpen}
          onClose={() => setIsListingTypeModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          listingTypes={config.listing.listingTypes}
          onContinue={selectedType => {
            history.push(`/l/new?listingType=${selectedType}`);
          }}
        />
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage">
        <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
      </NamedLink>
    </li>
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
       

        <ul className={css.accountLinksWrapper}>
          {/* <li className={classNames(css.navigationLink, currentPageClass('SearchPage'))}>
            <NamedLink name="SearchPage">
              Browse
            </NamedLink>
          </li>
          <li className={classNames(css.navigationLink, currentPageClass('NewListingPage'))}>
            <span
              onClick={() => setIsListingTypeModalOpen(true)}
              role="button"
              tabIndex={0}
            >
              Sell
            </span>
          </li>
          <li className={css.navigationLink}>
            <a href="/p/how-it-works">How It Works</a>
          </li>
          <li className={css.navigationLink}>
            <a href="/p/community">Community</a>
          </li> */}
          <li className={css.navigationLink}>
            <NamedLink title={intl.formatMessage({ id: 'Topbar.favouriteIcon' })} name="WishlistPage" className={classNames(css.favouriteMenueBtn,css.iconBtn)} data-testid="topbar-wishlist-btn">
                Wishlist
            </NamedLink>
          </li>
          <li className={css.navigationLink}>
            <span 
              onClick={() => {
                if (onLogout) {
                  try {
                    onLogout();
                  } catch (e) {}
                  history.push('/signup');
                }
              }} 
              style={{cursor: 'pointer'}}
            >
              <FormattedMessage id="TopbarMobileMenu.signupLink" />
            </span>
          </li>
        </ul>
        <ul className={css.customLinksWrapper}>{extraLinks}</ul>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <div className={css.spacer} />
       
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
      <ListingTypeModal
        isOpen={isListingTypeModalOpen}
        onClose={() => setIsListingTypeModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        listingTypes={config.listing.listingTypes}
        onContinue={selectedType => {
          history.push(`/l/new?listingType=${selectedType}`);
        }}
      />
    </div>
  );
};

export default TopbarMobileMenu;
